import { Alert, Button, Card, Col, DatePicker, Form, Pagination, Row, Select, Space, Table } from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { EventReportKeyColumnDescription, EventReportValueColumnDescription, KeyFilterClause } from 'src/api/api'
import { downloadCSVData, enumView, lowerize } from 'src/util/Util'
import Filters, { Filter } from './Filters'
import reportApi from './ReportApi'
import './style.css'

const { RangePicker } = DatePicker
const startFormat = 'YYYY-MM-DD 00:00:00'
const endFormat = 'YYYY-MM-DD 23:00:00'
const FormItem = Form.Item
const Option = Select.Option
const KeyColumns = EventReportKeyColumnDescription.ColumnEnum
const ValueColumns = EventReportValueColumnDescription.ColumnEnum
const OrderEnum = EventReportKeyColumnDescription.OrderEnum

const now = dayjs()
const allTimeStart = '2025-07-01 00:00:00'

const datesMap = new Map<string, any>([
    ['Today', [now.format(startFormat), now.format(endFormat)]],
    ['Yesterday', [now.subtract(1, 'day').format(startFormat), now.subtract(1, 'day').format(endFormat)]],
    ['Last 3 days', [now.subtract(3, 'day').format(startFormat), now.format(endFormat)]],
    ['Last 7 days', [now.subtract(7, 'day').format(startFormat), now.format(endFormat)]],
    ['Last 30 days', [now.subtract(30, 'day').format(startFormat), now.format(endFormat)]],
    ['This month', [now.startOf('month').format(startFormat), now.format(endFormat)]],
    ['Last month', [now.subtract(1, 'month').startOf('month').format(startFormat), now.subtract(1, 'month').endOf('month').format(endFormat)]],
    ['All time', [allTimeStart, now.format(endFormat)]],
    ['Custom range', []]
])

export const Report: React.FC = () => {

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(undefined)
    const [form] = Form.useForm()

    const [headers, setHeaders] = useState<string[]>()
    const [data, setData] = useState<string[][]>()
    const [totals, setTotals] = useState<{ [key: string]: number }>()
    const [reportId, setReportId] = useState<string | undefined>(undefined)

    const [totalLines, setTotalLines] = useState<number | undefined>(0)
    const [currentPage, setCurrentPage] = useState<number>(0)
    const [pageSize, setPageSize] = useState<number>(0)

    const [filters, setFilters] = useState<Filter[]>([])

    const isSelected = (cols: string[], col: EventReportKeyColumnDescription.ColumnEnum | EventReportValueColumnDescription.ColumnEnum) => {
        return cols.findIndex(item => item === col.toString()) !== -1
    }

    const isNotSelected = (cols: string[], col: EventReportKeyColumnDescription.ColumnEnum | EventReportValueColumnDescription.ColumnEnum) => {
        return !isSelected(cols, col)
    }

    const handleFilters = (res: EventReportKeyColumnDescription[]) => filters.forEach(filter => {
        filter.type && filter.values.length > 0 && res.push(
            {
                onlyFilter: true,
                column: filter.type,
                where: {
                    type: KeyFilterClause.TypeEnum.In,
                    value: filter.values
                }
            }
        )
    })

    const sortingApply = (
        sorter: SorterResult<any> | undefined,
        keyColumns: EventReportKeyColumnDescription[],
        valueColumns: EventReportValueColumnDescription[]
    ) => {
        if (sorter && sorter.column && sorter.order) {

            const column = sorter.column.title
            const order = sorter.order == 'ascend' ? OrderEnum.Asc : OrderEnum.Desc

            const byKey = keyColumns.some(key => {
                if (key.column == column) {
                    key.order = order
                    return true
                }
                return false
            })

            if (!byKey) {
                valueColumns.some(value => {
                    if (value.column == column) {
                        value.order = order
                        return true
                    }
                    return false
                })
            }
        }
    }

    const getKeyColumns = (cols: string[], formValues: any, sorter: SorterResult<any> | undefined) => {

        const res: EventReportKeyColumnDescription[] = []

        const date: EventReportKeyColumnDescription = {
            column: KeyColumns.DayHour,
            where: {
                type: KeyFilterClause.TypeEnum.Between,
                value: formValues.preDates == 'Custom range'
                    ? [formValues.dates[0].format(startFormat), formValues.dates[1].format(endFormat)]
                    : datesMap.get(formValues.preDates)
            }
        }

        if (!sorter) date.order = OrderEnum.Asc
        if (isNotSelected(cols, KeyColumns.DayHour)) date.onlyFilter = true

        res.push(date)

        if (isSelected(cols, KeyColumns.Year)) res.push({ column: KeyColumns.Year })
        if (isSelected(cols, KeyColumns.Month)) res.push({ column: KeyColumns.Month })
        if (isSelected(cols, KeyColumns.Week)) res.push({ column: KeyColumns.Week })
        if (isSelected(cols, KeyColumns.Day)) res.push({ column: KeyColumns.Day })
        if (isSelected(cols, KeyColumns.SSP)) res.push({ column: KeyColumns.SSP })
        if (isSelected(cols, KeyColumns.MediaAgency)) res.push({ column: KeyColumns.MediaAgency })
        if (isSelected(cols, KeyColumns.Advertiser)) res.push({ column: KeyColumns.Advertiser })
        if (isSelected(cols, KeyColumns.Campaign)) res.push({ column: KeyColumns.Campaign })
        if (isSelected(cols, KeyColumns.AdGroup)) res.push({ column: KeyColumns.AdGroup })
        if (isSelected(cols, KeyColumns.Creative)) res.push({ column: KeyColumns.Creative })
        if (isSelected(cols, KeyColumns.AdSize)) res.push({ column: KeyColumns.AdSize })
        if (isSelected(cols, KeyColumns.Country)) res.push({ column: KeyColumns.Country })
        if (isSelected(cols, KeyColumns.Language)) res.push({ column: KeyColumns.Language })
        if (isSelected(cols, KeyColumns.DeviceType)) res.push({ column: KeyColumns.DeviceType })
        if (isSelected(cols, KeyColumns.MediaType)) res.push({ column: KeyColumns.MediaType })
        if (isSelected(cols, KeyColumns.OS)) res.push({ column: KeyColumns.OS })
        if (isSelected(cols, KeyColumns.Domain)) res.push({ column: KeyColumns.Domain })
        if (isSelected(cols, KeyColumns.Bundle)) res.push({ column: KeyColumns.Bundle })
        if (isSelected(cols, KeyColumns.RequestType)) res.push({ column: KeyColumns.RequestType })
        if (isSelected(cols, KeyColumns.RequestAdSizes)) res.push({ column: KeyColumns.RequestAdSizes })
        if (isSelected(cols, KeyColumns.ErrorReason)) res.push({ column: KeyColumns.ErrorReason })
        if (isSelected(cols, KeyColumns.OptOut)) res.push({ column: KeyColumns.OptOut })

        handleFilters(res)

        return res
    }

    const getValueColumns = (cols: string[], formValues: any, sorter: SorterResult<any> | undefined) => {

        const res: EventReportValueColumnDescription[] = []

        if (!cols) return res

        if (isSelected(cols, ValueColumns.BidRequest)) res.push({ column: ValueColumns.BidRequest })
        if (isSelected(cols, ValueColumns.Request)) res.push({ column: ValueColumns.Request })
        if (isSelected(cols, ValueColumns.Bid)) res.push({ column: ValueColumns.Bid })
        if (isSelected(cols, ValueColumns.Win)) res.push({ column: ValueColumns.Win })
        if (isSelected(cols, ValueColumns.WinRate)) res.push({ column: ValueColumns.WinRate })
        if (isSelected(cols, ValueColumns.Impression)) res.push({ column: ValueColumns.Impression })
        if (isSelected(cols, ValueColumns.UniqImpression)) res.push({ column: ValueColumns.UniqImpression })
        if (isSelected(cols, ValueColumns.Click)) res.push({ column: ValueColumns.Click })
        if (isSelected(cols, ValueColumns.ErrorWin)) res.push({ column: ValueColumns.ErrorWin })
        if (isSelected(cols, ValueColumns.ErrorImpression)) res.push({ column: ValueColumns.ErrorImpression })
        if (isSelected(cols, ValueColumns.ErrorClick)) res.push({ column: ValueColumns.ErrorClick })
        if (isSelected(cols, ValueColumns.VastStart)) res.push({ column: ValueColumns.VastStart })
        if (isSelected(cols, ValueColumns.VastFirstQ)) res.push({ column: ValueColumns.VastFirstQ })
        if (isSelected(cols, ValueColumns.VastMidpoint)) res.push({ column: ValueColumns.VastMidpoint })
        if (isSelected(cols, ValueColumns.VastThirdQ)) res.push({ column: ValueColumns.VastThirdQ })
        if (isSelected(cols, ValueColumns.VastComplete)) res.push({ column: ValueColumns.VastComplete })
        if (isSelected(cols, ValueColumns.VastError)) res.push({ column: ValueColumns.VastError })
        if (isSelected(cols, ValueColumns.VastClose)) res.push({ column: ValueColumns.VastClose })
        if (isSelected(cols, ValueColumns.VastMute)) res.push({ column: ValueColumns.VastMute })
        if (isSelected(cols, ValueColumns.VastUnmute)) res.push({ column: ValueColumns.VastUnmute })
        if (isSelected(cols, ValueColumns.VastPause)) res.push({ column: ValueColumns.VastPause })
        if (isSelected(cols, ValueColumns.VastResume)) res.push({ column: ValueColumns.VastResume })
        if (isSelected(cols, ValueColumns.VastSkip)) res.push({ column: ValueColumns.VastSkip })
        if (isSelected(cols, ValueColumns.ErrorVastEvent)) res.push({ column: ValueColumns.ErrorVastEvent })
        if (isSelected(cols, ValueColumns.BidRate)) res.push({ column: ValueColumns.BidRate })
        if (isSelected(cols, ValueColumns.FillRate)) res.push({ column: ValueColumns.FillRate })
        if (isSelected(cols, ValueColumns.ClickThroughRate)) res.push({ column: ValueColumns.ClickThroughRate })
        if (isSelected(cols, ValueColumns.ViewThroughRate)) res.push({ column: ValueColumns.ViewThroughRate })
        if (isSelected(cols, ValueColumns.Cost)) res.push({ column: ValueColumns.Cost })
        if (isSelected(cols, ValueColumns.CPM)) res.push({ column: ValueColumns.CPM })
        if (isSelected(cols, ValueColumns.BidPrice)) res.push({ column: ValueColumns.BidPrice })
        if (isSelected(cols, ValueColumns.AvgBidPrice)) res.push({ column: ValueColumns.AvgBidPrice })
        if (isSelected(cols, ValueColumns.WinPrice)) res.push({ column: ValueColumns.WinPrice })
        if (isSelected(cols, ValueColumns.AvgWinPrice)) res.push({ column: ValueColumns.AvgWinPrice })
        if (isSelected(cols, ValueColumns.ErrorWinPrice)) res.push({ column: ValueColumns.ErrorWinPrice })
        if (isSelected(cols, ValueColumns.ImprPrice)) res.push({ column: ValueColumns.ImprPrice })
        if (isSelected(cols, ValueColumns.AvgImprPrice)) res.push({ column: ValueColumns.AvgImprPrice })
        if (isSelected(cols, ValueColumns.ErrorImprPrice)) res.push({ column: ValueColumns.ErrorImprPrice })
        if (isSelected(cols, ValueColumns.CostPerClick)) res.push({ column: ValueColumns.CostPerClick })

        return res
    }

    const requestReport = async (sorter: SorterResult<any> | undefined) => {
        const values = await form.validateFields()

        const keys = values.keys as string[]
        const columns = values.columns as string[]

        const allColumns = ([] as string[]).concat(keys, columns)

        const keyColumns = getKeyColumns(allColumns, values, sorter)
        const valueColumns = getValueColumns(allColumns, values, sorter)

        sortingApply(sorter, keyColumns, valueColumns)

        try {
            const created = await reportApi.createReport({ keyColumns, valueColumns })
            return created.data
        } catch (e) {
            const message = await e.json()
            setError(message.error)
            return undefined
        }
    }

    const readReport = async (ready: Response) => {
        const blob = await ready.blob()
        const text = await blob.text()

        if (text.length < 1) return null

        const rows = text.split('\n')
        if (rows.length < 2) return null

        return rows
    }

    // TODO: temp
    const parseCSVRow = (row: string) => {

        const result: string[] = []
        let inQuotes = false
        let current = ''

        for (let i = 0; i < row.length; i++) {
            const char = row[i]

            if (char == '"') {
                inQuotes = !inQuotes
            } else if (char == ',' && !inQuotes) {
                result.push(current)
                current = ''
            } else {
                current += char
            }
        }

        result.push(current)
        return result
    }

    const handleReport = async () => {
        handleSortedReport(undefined)
    }

    const handleSortedReport = async (sorter: SorterResult<any> | undefined) => {

        setLoading(true)

        if (!sorter) {
            setData(undefined)
            setTotals(undefined)
        }

        const uuid = await requestReport(sorter)

        if (!uuid) {
            setLoading(false)
            return
        }

        setReportId(uuid)

        const interval = setInterval(async () => {
            try {
                const ready = await reportApi.get(uuid)

                clearInterval(interval)

                const rows = await readReport(ready)
                if (rows == null) return

                setHeaders(rows[0].split(','))

                rows.shift()
                rows.pop()

                // setData(rows.map(row => row.split(',')))
                setData(rows.map(parseCSVRow))
                setLoading(false)

                if (rows.length > 1) {
                    const reportInfo = await reportApi.info(uuid)
                    const reportBean = reportInfo.data
                    setTotals(reportBean?.totalValues)
                    setTotalLines(reportBean?.totalLines)
                    setCurrentPage(1)
                    setPageSize(50)
                }
            } catch (response) {
                if (typeof response === 'object') {
                    if (response.status !== 425) {
                        setLoading(false)
                        clearInterval(interval)
                    }
                }
            }
        }, 500)
    }

    const loadReportPage = async (page: number, size: number) => {
        try {
            const ready = await reportApi.get(reportId ?? '', size, page)

            const rows = await readReport(ready)
            if (rows == null) return

            setHeaders(rows[0].split(','))

            rows.shift()
            rows.pop()

            // setData(rows.map(row => row.split(',')))
            setData(rows.map(parseCSVRow))
            setLoading(false)

            setCurrentPage(page)
            setPageSize(size)
        } catch (e) {
            setLoading(false)
        }
    }

    const handleCsv = async () => {
        if (reportId) {
            const response = await reportApi.csv(reportId)
            const blob = await response.blob()
            downloadCSVData('report.csv', blob)
            return
        }

        setLoading(true)

        const uuid = await requestReport(undefined)

        if (!uuid) {
            setLoading(false)
            return
        }

        setReportId(uuid)

        const interval = setInterval(async () => {
            try {
                const ready = await reportApi.csv(uuid)

                clearInterval(interval)

                setLoading(false)

                const blob = await ready.blob();
                downloadCSVData('report.csv', blob);
            } catch (response) {
                if (typeof response === 'object') {
                    if (response.status !== 425) {
                        setLoading(false)
                        clearInterval(interval)
                    }
                }
            }
        }, 500)
    }

    const headerFunc = (headersToProcess?: any[]) => {
        return headersToProcess?.map(header => ({
            title: header,
            dataIndex: header.toLowerCase(),
            key: header.toLowerCase(),
            sorter: true
        }))
    }

    const sourceFunc = (dataToProcess?: any[], headersToProcess?: any[], totalsToProcess?: any) => {
        const source = dataToProcess?.map((row, rowIndex) => headersToProcess?.reduce((prev, current, columnIndex) => ({
            ...prev,
            [current.toLowerCase() ?? '']: row[columnIndex],
            key: rowIndex
        }), {}) ?? {})

        totalsToProcess && source?.push(lowerize(totalsToProcess))

        return source
    }

    const renderPagination = () => {
        return totalLines && totalLines > pageSize ? <Pagination
            current={currentPage}
            onChange={loadReportPage}
            pageSize={pageSize}
            total={totalLines} /> : null
    }

    const dateType = Form.useWatch('preDates', form)

    return <div>
        <Form
            style={{
                maxWidth: '1000px', margin: '0 auto'
            }}
            onFinish={handleReport}
            form={form}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
        >
            {error && <Alert message={error} type="error" />}
            <Card size="small" className="form-card">
                <FormItem
                    label="Dates"
                >
                    <FormItem
                        name="preDates"
                        className="ant-item ant-item-first pre-dates-selector"
                    >
                        <Select
                            allowClear
                            listHeight={300}
                            placeholder="Select dates"
                        >
                            {Array.from(datesMap).map(([key, value]) => <Option title={key} key={key} value={key}>{key}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="dates"
                        className="ant-item ant-item-second"
                    >
                        <RangePicker disabled={dateType != 'Custom range'} format="YYYY-MM-DD" />
                    </FormItem>
                </FormItem>
                <FormItem
                    name="keys"
                    label="Dimensions"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select dimensions"
                    >
                        {Object.keys(KeyColumns).sort().map(key => <Option title={key} key={key} value={key}>{enumView(key)}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="columns"
                    label="Metrics"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select metrics"
                    >
                        {Object.keys(ValueColumns).sort().map(value => <Option title={value} key={value} value={value}>{enumView(value)}</Option>)}
                    </Select>
                </FormItem>
                <Filters
                    filters={filters}
                    setFilters={setFilters}
                />
                <FormItem
                    wrapperCol={{
                        offset: 3, span: 21
                    }}>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={loading}>Generate report</Button>
                        <Button type="default" htmlType="button" loading={loading} onClick={handleCsv}>Download CSV</Button>
                    </Space>
                </FormItem>
            </Card>
        </Form>

        {data && <Table
            pagination={false}
            columns={headerFunc(headers)}
            dataSource={sourceFunc(data, headers, totals)}
            onChange={(p, f, sorter) => handleSortedReport(sorter as SorterResult<any>)}
        />}
        <Row>
            <Col span={24} style={{ display: 'flex', justifyContent: 'center', marginTop: '1%' }}>
                {renderPagination()}
            </Col>
        </Row>
    </div>
}


// WEBPACK FOOTER //
// ./src/component/app/report/Report.tsx